import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, BoxProps, Flex, useMultiStyleConfig, Text, VStack} from '@chakra-ui/react'
import {Button, FormPrivacyDisclaimer, Icon} from '_uiComponents'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useForm} from 'react-hook-form'
import {FormContactRecaptcha, SubmitContactResponse} from '_types'
import {useCustomApi, useTencentCaptcha} from '_customHooks'
import {FormattedMessage, useIntl} from 'react-intl'
import {FormFactory, FormValues} from '_factories'
import {useLoader, useGlobalConfigState} from '_context'
import {analyticsEventContactUs} from '_analytics/events'

interface ContactUsProps {
    localeID: string
    productId: string
    onSubmitForm?: () => void
    // Values forced into the submitted data without rendering an input for them (e.g. infocategory hides the category select)
    hiddenFormValues?: Record<string, string>
}

/**
 * @name ContactUs
 *
 * @component
 *
 * @description
 * Renders the product contact us component.
 *
 * @returns {React.FC} - The rendered product contact us component.
 */
const ContactUs: React.FC<ContactUsProps> = ({localeID, productId, onSubmitForm, hiddenFormValues}: ContactUsProps) => {
    const {executeRecaptcha} = useGoogleReCaptcha()
    const {isTencentEnabled, isTencentReady, tencentCaptchaResult, triggerTencentCaptcha, resetTencentCaptcha} = useTencentCaptcha()

    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {showLoader, hideLoader} = useLoader()
    const {appPreferences} = useGlobalConfigState()
    const {useGetForm, useSubmitForm} = useCustomApi()

    const {mutateAsync: getFormMutation} = useGetForm()
    const {mutateAsync: submitFormMutation} = useSubmitForm()
    const isRecaptchaActive = !!appPreferences.apikeyv3_recaptcha

    const [contactUsForm, setContactUsForm] = useState<FormContactRecaptcha>()
    const [isSubmitError, setIsSubmitError] = useState<boolean>(false)
    const [isSubmitSuccess, setIsSubmitSuccess] = useState<boolean>(false)

    const recaptchaToken = useRef<string>()
    const tencentToken = useRef<string>()
    const tencentRandstr = useRef<string>()
    const pendingFormData = useRef<FormValues | null>(null)

    const {
        control,
        formState: {errors},
        handleSubmit,
    } = useForm<FormValues>()

    const styles = useMultiStyleConfig('ProductContactUs', {}) as Record<string, BoxProps>

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return
        }
        const googleReCaptchaToken = await executeRecaptcha('productContactUsForm')
        if (!recaptchaToken.current) recaptchaToken.current = googleReCaptchaToken
    }, [executeRecaptcha])

    const triggerTencentValidation = () => {
        if (isTencentReady) {
            triggerTencentCaptcha()
        }
    }

    const doSubmit = (formData: object) => {
        const reason = (formData as {infocategory?: string})?.infocategory
        showLoader()
        submitFormMutation({locale: locale.id, formId: 'contactRecaptcha', formData})
            .then((response: SubmitContactResponse) => {
                // Per the Form API contract, success is signalled by a top-level `success: true` flag.
                // A failed CAPTCHA (and any other failure) returns HTTP 200 without that flag — the
                // same convention the PWA Newsletter follows — so it must be treated as an error, not
                // as a successful submit.
                const isSuccess = (response as {success?: boolean})?.success === true || ('response' in response && response.response?.object?.success === true)
                setIsSubmitError(() => !isSuccess)
                setIsSubmitSuccess(() => isSuccess)
                if (isSuccess) onSubmitForm?.()
                analyticsEventContactUs({action: isSuccess ? 'submit_ok' : 'submit_fail', reason, productId})
            })
            .catch(() => {
                setIsSubmitError(() => true)
                analyticsEventContactUs({action: 'submit_fail', reason, productId})
            })
            .finally(() => {
                hideLoader()
                resetCaptchaTokens()
                resetTencentCaptcha()
            })
    }

    const submitContactUs = (data) => {
        const dataWithHiddenValues = hiddenFormValues ? {...data, ...hiddenFormValues} : data
        if (isTencentEnabled) {
            pendingFormData.current = dataWithHiddenValues
            triggerTencentValidation()
            return
        }
        const formData = isRecaptchaActive ? {...dataWithHiddenValues, 'g-recaptcha-response': recaptchaToken.current} : dataWithHiddenValues
        doSubmit(formData)
    }

    const resetCaptchaTokens = () => {
        recaptchaToken.current = ''
        tencentToken.current = ''
        tencentRandstr.current = ''
    }

    useEffect(() => {
        if (isTencentEnabled && tencentCaptchaResult) {
            tencentToken.current = tencentCaptchaResult.ticket
            tencentRandstr.current = tencentCaptchaResult.randstr

            if (pendingFormData.current) {
                const formData = {...pendingFormData.current, tencent_captcha_ticket: tencentToken.current, tencent_captcha_randstr: tencentRandstr.current}
                pendingFormData.current = null
                doSubmit(formData)
            }
        } else if (!isTencentEnabled) {
            void handleReCaptchaVerify()
        }
    }, [tencentCaptchaResult, isTencentEnabled])

    useEffect(() => {
        showLoader()
        getFormMutation({locale: locale.id, formId: 'contactRecaptcha'})
            .then((response) => setContactUsForm(() => response.form))
            .catch(() => {})
            .finally(hideLoader)
    }, [])

    return (
        <VStack {...styles.wrapper}>
            {contactUsForm && !isSubmitError && !isSubmitSuccess && (
                <>
                    <VStack {...styles.textContainer}>
                        <Text {...styles.mayWeHelp}>
                            <FormattedMessage
                                id="label.contact.explain.text"
                                defaultMessage={'Our Customer Care will be happy to assist you with your requests.'}
                            />
                        </Text>
                        <Text {...styles.requiredFieldsText}>
                            <FormattedMessage id="label.contact.form.required.fields" defaultMessage={'Fields marked with an asterisk (*) are required'} />
                        </Text>
                    </VStack>

                    <VStack {...styles.inputContainer} as="form" noValidate onSubmit={handleSubmit((data) => submitContactUs(data))}>
                        {!hiddenFormValues?.infocategory && contactUsForm.product?.infocategory && (
                            <FormFactory
                                type="select"
                                control={control}
                                errors={errors}
                                isRequired={contactUsForm.product?.infocategory.mandatory}
                                label={contactUsForm.product?.infocategory.label + '*'}
                                name={'infocategory'}
                                options={
                                    contactUsForm.product?.infocategory.options?.map((option, index) => ({label: option.label, value: option.id, index})) || []
                                }
                                placeholder={formatMessage({id: 'resource.select', defaultMessage: 'Select...'})}
                            />
                        )}

                        {contactUsForm.product?.sku && (
                            <FormFactory
                                type="text"
                                control={control}
                                errors={errors}
                                name={'contactSku'}
                                label={contactUsForm.product?.sku.label + '*'}
                                defaultValue={productId}
                                value={productId}
                                isReadOnly={true}
                                isRequired={false}
                                maxLength={contactUsForm.product?.sku.maxLength}
                            />
                        )}

                        {contactUsForm.product.gender && (
                            <FormFactory
                                type="select"
                                control={control}
                                errors={errors}
                                name={'genderSelect'}
                                label={contactUsForm.product.gender.label + (contactUsForm.product.gender.mandatory ? '*' : '')}
                                isRequired={contactUsForm.product.gender.mandatory}
                                options={
                                    contactUsForm.product.gender.options
                                        ?.filter((option) => {
                                            // remove Mrs. gender option if language is not 'en'
                                            if (!locale.id.startsWith('en')) {
                                                return option.value !== 'Mrs.'
                                            }
                                            return true
                                        })
                                        ?.map((option, index) => ({label: option.label, value: option.id, index})) || []
                                }
                            />
                        )}

                        {contactUsForm.product.firstname && (
                            <FormFactory
                                type="text"
                                control={control}
                                errors={errors}
                                name={'contactFirstName'}
                                label={contactUsForm.product.firstname.label}
                                isRequired={contactUsForm.product.firstname.mandatory}
                                maxLength={contactUsForm.product.firstname.maxLength}
                            />
                        )}

                        {contactUsForm.product.lastname && (
                            <FormFactory
                                type="text"
                                control={control}
                                errors={errors}
                                name={'contactLastName'}
                                label={contactUsForm.product.lastname.label}
                                isRequired={contactUsForm.product.lastname.mandatory}
                                maxLength={contactUsForm.product.lastname.maxLength}
                            />
                        )}

                        {contactUsForm.product.email && (
                            <FormFactory
                                type="email"
                                control={control}
                                errors={errors}
                                name={'contactEmail'}
                                label={contactUsForm.product.email.label}
                                isRequired={contactUsForm.product.email.mandatory}
                                maxLength={contactUsForm.product.email.maxLength}
                                pattern={contactUsForm.product.email.regEx}
                            />
                        )}

                        {contactUsForm.product.phone && (
                            <FormFactory
                                type="text"
                                control={control}
                                errors={errors}
                                name={'contactPhone'}
                                label={contactUsForm.product.phone.label + (contactUsForm.product.phone.mandatory ? '*' : '')}
                                isRequired={contactUsForm.product.phone.mandatory}
                                minLength={contactUsForm.product.phone.minLength}
                                maxLength={contactUsForm.product.phone.maxLength}
                                pattern={contactUsForm.product.phone.regEx}
                            />
                        )}

                        {contactUsForm.product.message && (
                            <FormFactory
                                type="textarea"
                                control={control}
                                errors={errors}
                                name={'contactMessage'}
                                label={contactUsForm.product.message.label + (contactUsForm.product.message.mandatory ? '*' : '')}
                                isRequired={false}
                                pattern={contactUsForm.product.message.regEx}
                            />
                        )}

                        <FormPrivacyDisclaimer
                            privacyMessageProps={styles.privacyMessage}
                            recaptchaMessageProps={styles.recaptchaMessage}
                            containerProps={styles.privacyContainer}
                        />
                        <Button variant="primary" type="submit">
                            <FormattedMessage id="label.pdp.sandmessage" defaultMessage={'SEND MESSAGE'} />
                        </Button>
                    </VStack>
                </>
            )}

            {isSubmitError && (
                <Flex {...styles.errorContainer}>
                    {/* Icon color is a literal hex (design-system error #922222): Icon paints the SVG
                        stroke directly with this value, so a Chakra token string would not resolve. */}
                    <Icon size="sm" name="IconInfo" color="#922222" sx={{flexShrink: 0}} />
                    <Text {...styles.errorMessage}>
                        <FormattedMessage id="label.contactUs.error.message" defaultMessage={'An error has occurred. Please try again later'} />
                    </Text>
                </Flex>
            )}

            {isSubmitSuccess && (
                <VStack {...styles.successContainer}>
                    <Text {...styles.successMessage}>
                        <FormattedMessage id="label.form.success.message" defaultMessage={'You will receive updates via e-mail as soon as possible.'} />
                    </Text>
                </VStack>
            )}
        </VStack>
    )
}

ContactUs.displayName = 'ContactUs'

export default ContactUs
